"use client";

import { Package, Truck, X } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getMyShopOrders, type MyShopOrder } from "@/actions/shop";
import { CommerceDocumentLinks } from "@/components/pricing/commerce-document-links.client";
import { PricingBreakdown } from "@/components/pricing/pricing-breakdown";
import { AppImage as Image } from "@/components/ui/app-image";
import type { ShopOrderStatus } from "@/db/schema/shop";
import { mediaUrlFromAsset } from "@/lib/media";
import { cn } from "@/lib/utils";
import { formatMoney } from "./shop-format";

const springTransition = {
  type: "spring" as const,
  stiffness: 400,
  damping: 35,
};

const STATUS_META: Record<
  ShopOrderStatus,
  { label: string; className: string }
> = {
  pending_payment: {
    label: "Payment pending",
    className: "bg-warning/15 text-warning",
  },
  paid: { label: "Paid", className: "bg-brand/15 text-brand" },
  fulfilled: { label: "Fulfilled", className: "bg-success/15 text-success" },
  cancelled: {
    label: "Cancelled",
    className: "bg-surface-alt text-text-tertiary",
  },
  expired: { label: "Expired", className: "bg-surface-alt text-text-tertiary" },
};

export function MyOrdersSheet({ onClose }: { onClose: () => void }) {
  const [orders, setOrders] = useState<MyShopOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void getMyShopOrders().then((result) => {
      if (!active) return;
      if (result.ok) setOrders(result.data);
      else setError(result.message);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex flex-col items-center justify-end sm:justify-center">
      <motion.div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
      />
      <motion.div
        className="relative z-10 flex max-h-[92%] w-full max-w-md flex-col overflow-hidden bg-background shadow-2xl sm:rounded-3xl"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={springTransition}
      >
        <div className="flex items-center justify-between px-5 pt-[max(env(safe-area-inset-top),1rem)] pb-3">
          <h2 className="text-lg font-semibold text-text-primary">My orders</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-alt text-text-secondary transition-colors hover:bg-surface-elevated"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-[max(env(safe-area-inset-bottom),1rem)]">
          {error ? (
            <p className="py-10 text-center text-sm text-text-secondary">
              {error}
            </p>
          ) : orders === null ? (
            <div className="space-y-3 py-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-20 animate-pulse rounded-2xl bg-surface-alt"
                />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <Package className="h-8 w-8 text-text-tertiary" />
              <p className="text-sm text-text-secondary">No orders yet.</p>
            </div>
          ) : (
            <div className="space-y-3 py-1">
              {orders.map((order) => (
                <OrderRow key={order.id} order={order} />
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}

function OrderRow({ order }: { order: MyShopOrder }) {
  const status = STATUS_META[order.status];
  const image = order.itemImage ? mediaUrlFromAsset(order.itemImage) : null;
  return (
    <div className="flex gap-3 rounded-2xl border border-border/70 bg-surface p-3">
      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-surface-alt">
        {image ? (
          <Image
            src={image}
            alt={order.itemName}
            fill
            sizes="56px"
            className="object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-text-tertiary">
            <Package className="h-5 w-5" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="truncate text-sm font-medium text-text-primary">
            {order.itemName}
          </p>
          <span
            className={cn(
              "text-2xs shrink-0 rounded-full px-2 py-0.5 font-medium",
              status.className,
            )}
          >
            {status.label}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-text-secondary">
          Qty {order.quantity} ·{" "}
          {formatMoney(
            order.totalAmountMinor + order.creditsAppliedMinor,
            order.currency,
          )}
        </p>
        {order.pricingSnapshot ? (
          <div className="my-2 space-y-2">
            <details>
              <summary className="cursor-pointer text-xs">
                Price breakdown
              </summary>
              <PricingBreakdown snapshot={order.pricingSnapshot} />
            </details>
            <CommerceDocumentLinks
              sourceType="shop_order"
              sourceId={order.id}
            />
          </div>
        ) : null}
        {order.creditsAppliedMinor > 0 ? (
          <p className="mt-0.5 text-xs text-brand">
            {formatMoney(order.creditsAppliedMinor, order.currency)} paid with
            Hiive Money
          </p>
        ) : null}
        <div className="mt-1 flex items-center gap-1.5 text-xs text-text-tertiary">
          {order.fulfillmentType === "delivery" ? (
            <>
              <Truck className="h-3.5 w-3.5" />
              {order.status === "fulfilled" && order.trackingNumber
                ? `${order.trackingCarrier ?? "Shipped"} · ${order.trackingNumber}`
                : "Delivery"}
            </>
          ) : (
            <>
              <Package className="h-3.5 w-3.5" /> Collect in person
            </>
          )}
        </div>
      </div>
    </div>
  );
}
